import React, { useState, useRef, useEffect } from 'react';
import { ShoppingBag, User, Heart, Search, Menu, X, ArrowRight, ChevronRight, ChevronDown, Play, Star, Plus, Instagram, Truck, RotateCcw, Award, Gift } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Template18 = () => {
    const navigate = useNavigate();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [hoveredProduct, setHoveredProduct] = useState(null);
    const [activeTestimonial, setActiveTestimonial] = useState(0);
    const [wishlist, setWishlist] = useState({});
    const touchStartX = useRef(0);

    // --- ORIGINAL data (desktop) ---
    const heroSlides = [
        { image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1920&h=1080&fit=crop', subtitle: 'New Collection', title: 'Autumn Winter 2025', cta: 'Discover Now' },
        { image: 'https://images.unsplash.com/photo-1509631179647-0177331693ae?w=1920&h=1080&fit=crop', subtitle: 'Limited Edition', title: 'Designer Series', cta: 'Shop Collection' },
    ];

    const desktopCategories = [
        { name: 'Dresses', count: 156, image: 'https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?w=600&h=800&fit=crop' },
        { name: 'Separates', count: 234, image: 'https://images.unsplash.com/photo-1496747611176-843222e1e57c?w=600&h=800&fit=crop' },
        { name: 'Outerwear', count: 89, image: 'https://images.unsplash.com/photo-1509631179647-0177331693ae?w=600&h=800&fit=crop' },
        { name: 'Occasion', count: 124, image: 'https://images.unsplash.com/photo-1485968579580-b6d095142e6e?w=600&h=800&fit=crop' },
    ];

    const desktopProducts = [
        { name: 'Silk Wrap Dress', price: 18500, color: 'Midnight', image: 'https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?w=500&h=700&fit=crop', hoverImage: 'https://images.unsplash.com/photo-1496747611176-843222e1e57c?w=500&h=700&fit=crop', isNew: true },
        { name: 'Tailored Blazer', price: 24000, color: 'Charcoal', image: 'https://images.unsplash.com/photo-1509631179647-0177331693ae?w=500&h=700&fit=crop', hoverImage: 'https://images.unsplash.com/photo-1485968579580-b6d095142e6e?w=500&h=700&fit=crop', isNew: true },
        { name: 'Pleated Midi Skirt', price: 12500, color: 'Champagne', image: 'https://images.unsplash.com/photo-1485968579580-b6d095142e6e?w=500&h=700&fit=crop', hoverImage: 'https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?w=500&h=700&fit=crop', isNew: false },
        { name: 'Cashmere Sweater', price: 19800, color: 'Ivory', image: 'https://images.unsplash.com/photo-1558171813-4c088753af8f?w=500&h=700&fit=crop', hoverImage: 'https://images.unsplash.com/photo-1564257631407-4deb1f99d992?w=500&h=700&fit=crop', isNew: false },
        { name: 'Embroidered Kurta', price: 15500, color: 'Sage', image: 'https://images.unsplash.com/photo-1583391733956-3750e0ff4e8b?w=500&h=700&fit=crop', hoverImage: 'https://images.unsplash.com/photo-1610030469983-98e550d6193c?w=500&h=700&fit=crop', isNew: true },
        { name: 'Wide Leg Trousers', price: 13200, color: 'Black', image: 'https://images.unsplash.com/photo-1594938298603-c8148c4dae35?w=500&h=700&fit=crop', hoverImage: 'https://images.unsplash.com/photo-1506629082955-511b1aa562c8?w=500&h=700&fit=crop', isNew: false },
        { name: 'Satin Blouse', price: 11800, color: 'Blush', image: 'https://images.unsplash.com/photo-1564257631407-4deb1f99d992?w=500&h=700&fit=crop', hoverImage: 'https://images.unsplash.com/photo-1558171813-4c088753af8f?w=500&h=700&fit=crop', isNew: false },
        { name: 'Linen Dress', price: 16500, color: 'Natural', image: 'https://images.unsplash.com/photo-1594040226829-7f251ab46d80?w=500&h=700&fit=crop', hoverImage: 'https://images.unsplash.com/photo-1595950653106-6c9ebd614d3a?w=500&h=700&fit=crop', isNew: true },
    ];

    const desktopTestimonials = [
        { text: 'The quality is exceptional. Every piece feels luxurious and fits perfectly.', name: 'Meera Kapoor', city: 'Mumbai', rating: 5 },
        { text: 'Avante has become my go-to for elegant occasion wear. Impeccable service.', name: 'Ananya Sharma', city: 'Delhi', rating: 5 },
        { text: 'Finally found a brand that understands sophisticated, modern Indian fashion.', name: 'Priya Iyer', city: 'Bangalore', rating: 5 },
    ];

    // --- CURRENT data (mobile) ---
    const mobileCategories = [
        { name: 'Vintage Dresses', image: 'https://images.unsplash.com/photo-1496747611176-843222e1e57c?w=600&h=800&fit=crop', tagline: 'Timeless silhouettes' },
        { name: 'Retro Separates', image: 'https://images.unsplash.com/photo-1509631179647-0177331693ae?w=600&h=800&fit=crop', tagline: 'Mix & match classics' },
        { name: 'Heritage Sarees', image: 'https://images.unsplash.com/photo-1583391733956-6c78276477e2?w=600&h=800&fit=crop', tagline: 'Woven traditions' },
        { name: 'Throwback Denim', image: 'https://images.unsplash.com/photo-1542272604-787c3835535d?w=600&h=800&fit=crop', tagline: 'Old-school cool' },
    ];

    const mobileProducts = [
        { name: 'Polka Dot Swing Dress', price: 4500, image: 'https://images.unsplash.com/photo-1595777457583-95e059d581b8?w=500&h=650&fit=crop', tag: 'Bestseller', color: 'Cherry Red' },
        { name: 'High-Waist Palazzo', price: 3200, image: 'https://images.unsplash.com/photo-1584917865442-de89df76afd3?w=500&h=650&fit=crop', tag: null, color: 'Mustard' },
        { name: 'Vintage Floral Maxi', price: 5800, image: 'https://images.unsplash.com/photo-1543163521-1bf539c55dd2?w=500&h=650&fit=crop', tag: 'New', color: 'Dusty Rose' },
        { name: 'Retro Collar Blouse', price: 2800, image: 'https://images.unsplash.com/photo-1564257631407-4deb1f99d992?w=500&h=650&fit=crop', tag: null, color: 'Cream' },
        { name: 'Pin-Tuck Midi Skirt', price: 3900, image: 'https://images.unsplash.com/photo-1591047139829-d91aecb6caea?w=500&h=650&fit=crop', tag: 'Limited', color: 'Teal' },
        { name: 'Corduroy Jacket', price: 6200, image: 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=500&h=650&fit=crop', tag: null, color: 'Burnt Sienna' },
    ];

    const mobileTestimonials = [
        { text: 'Every piece feels like it carries a story. The vintage-inspired prints and quality fabrics remind me of my grandmother\'s wardrobe, but with a modern twist.', name: 'Kavya Nair', city: 'Kochi', rating: 5 },
        { text: 'I fell in love with the retro aesthetic instantly. The polka dot dress is my absolute favorite -- I get compliments every single time I wear it.', name: 'Shalini Gupta', city: 'Jaipur', rating: 5 },
        { text: 'Shopping here is like stepping into a time capsule. The curation is impeccable and the quality speaks of a bygone era of craftsmanship.', name: 'Meera Iyer', city: 'Chennai', rating: 5 },
    ];

    const features = [
        { icon: Truck, title: 'Free Shipping', desc: 'Over Rs.2,000' },
        { icon: RotateCcw, title: 'Easy Returns', desc: '15-day window' },
        { icon: Award, title: 'Authentic Vintage', desc: 'Curated pieces' },
        { icon: Gift, title: 'Gift Wrapping', desc: 'Retro style' },
    ];

    const instagramPosts = [
        'https://images.unsplash.com/photo-1496747611176-843222e1e57c?w=400&h=400&fit=crop',
        'https://images.unsplash.com/photo-1529139574466-a303027c1d8b?w=400&h=400&fit=crop',
        'https://images.unsplash.com/photo-1469334031218-e382a71b716b?w=400&h=400&fit=crop',
        'https://images.unsplash.com/photo-1509631179647-0177331693ae?w=400&h=400&fit=crop',
        'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?w=400&h=400&fit=crop',
        'https://images.unsplash.com/photo-1583391733956-6c78276477e2?w=400&h=400&fit=crop',
    ];

    useEffect(() => {
        const heroTimer = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
        }, 6000);
        return () => clearInterval(heroTimer);
    }, [heroSlides.length]);

    useEffect(() => {
        const timer = setInterval(() => {
            setActiveTestimonial(prev => (prev + 1) % mobileTestimonials.length);
        }, 5000);
        return () => clearInterval(timer);
    }, [mobileTestimonials.length]);

    const handleTestimonialSwipe = (e, type) => {
        if (type === 'start') touchStartX.current = e.touches[0].clientX;
        if (type === 'end') {
            const diff = touchStartX.current - e.changedTouches[0].clientX;
            if (Math.abs(diff) > 50) {
                setActiveTestimonial(prev => diff > 0 ? (prev + 1) % mobileTestimonials.length : (prev - 1 + mobileTestimonials.length) % mobileTestimonials.length);
            }
        }
    };

    return (
        <div className="w-full min-h-screen bg-white font-sans">
            {/* ==================== TOP BAR ==================== */}
            {/* Mobile Top Bar */}
            <div className="lg:hidden bg-amber-900 text-amber-100 text-center py-2 text-[10px] sm:text-xs tracking-[0.2em] px-4" style={{ fontFamily: "'Georgia', serif" }}>
                FREE SHIPPING ON ORDERS OVER Rs.2,000 | VINTAGE INSPIRED, MODERN CRAFTED
            </div>
            {/* Desktop Top Bar - EXACT original */}
            <div className="hidden lg:block bg-zinc-900 text-white text-center py-2.5 text-xs tracking-[0.2em]">
                COMPLIMENTARY SHIPPING ON ORDERS OVER ₹5,000
            </div>

            {/* ==================== HEADER ==================== */}
            {/* Mobile Header */}
            <header className="lg:hidden bg-amber-50/80 backdrop-blur-sm sticky top-0 z-50 border-b border-amber-200/50">
                <div className="max-w-screen-2xl mx-auto">
                    <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4">
                        <button className="min-w-[44px] min-h-[44px] flex items-center justify-center" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                            {mobileMenuOpen ? <X className="w-5 h-5 text-amber-900" /> : <Menu className="w-5 h-5 text-amber-900" />}
                        </button>
                        <h1 onClick={() => navigate('/template18')} className="text-xl sm:text-2xl text-amber-900 cursor-pointer tracking-[0.15em]" style={{ fontFamily: "'Georgia', serif", fontWeight: 400 }}>
                            REVERIE
                        </h1>
                        <div className="flex items-center gap-3 sm:gap-5">
                            <button className="min-w-[44px] min-h-[44px] flex items-center justify-center"><Search className="w-5 h-5 text-amber-800 hover:text-amber-950 transition-colors" /></button>
                            <button className="min-w-[44px] min-h-[44px] hidden sm:flex items-center justify-center" onClick={() => navigate('/template18/profile')}><User className="w-5 h-5 text-amber-800 hover:text-amber-950 transition-colors" /></button>
                            <button className="relative min-w-[44px] min-h-[44px] flex items-center justify-center" onClick={() => navigate('/template18/cart')}>
                                <ShoppingBag className="w-5 h-5 text-amber-800 hover:text-amber-950 transition-colors" />
                                <span className="absolute top-1 right-0.5 w-4 h-4 bg-amber-900 text-white text-[10px] rounded-full flex items-center justify-center">0</span>
                            </button>
                        </div>
                    </div>
                </div>
                <div className={`bg-amber-50 border-t border-amber-200/50 overflow-hidden transition-all duration-300 ${mobileMenuOpen ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0'}`}>
                    <nav className="px-5 py-6 space-y-1">
                        <button onClick={() => { navigate('/template18/shop'); setMobileMenuOpen(false); }} className="block w-full text-left py-3 text-base text-amber-900 border-b border-amber-100">Shop</button>
                        <button onClick={() => setMobileMenuOpen(false)} className="block w-full text-left py-3 text-base text-amber-900 border-b border-amber-100">New In</button>
                        <button onClick={() => setMobileMenuOpen(false)} className="block w-full text-left py-3 text-base text-amber-900 border-b border-amber-100">Collections</button>
                        <button onClick={() => { navigate('/template18/contact'); setMobileMenuOpen(false); }} className="block w-full text-left py-3 text-base text-amber-900 border-b border-amber-100">Contact</button>
                        <div className="flex gap-4 pt-4">
                            <button onClick={() => { navigate('/template18/profile'); setMobileMenuOpen(false); }} className="flex items-center gap-2 text-sm text-amber-700"><User className="w-4 h-4" /> Account</button>
                            <button className="flex items-center gap-2 text-sm text-amber-700"><Heart className="w-4 h-4" /> Wishlist</button>
                        </div>
                    </nav>
                </div>
            </header>

            {/* Desktop Header - EXACT original */}
            <header className="hidden lg:block bg-white sticky top-0 z-50 border-b border-zinc-100">
                <div className="max-w-screen-2xl mx-auto">
                    <div className="flex items-center justify-between px-6 lg:px-12 py-4">
                        {/* Desktop Nav Left */}
                        <nav className="flex items-center gap-8">
                            <button onClick={() => navigate('/template18/shop')} className="text-sm text-zinc-700 hover:text-zinc-900 transition-colors tracking-wide flex items-center gap-1">
                                Shop <ChevronDown className="w-3 h-3" />
                            </button>
                            <button className="text-sm text-zinc-700 hover:text-zinc-900 transition-colors tracking-wide">
                                New Arrivals
                            </button>
                            <button className="text-sm text-zinc-700 hover:text-zinc-900 transition-colors tracking-wide">
                                Collections
                            </button>
                            <button className="text-sm text-zinc-700 hover:text-zinc-900 transition-colors tracking-wide">
                                The Edit
                            </button>
                        </nav>

                        {/* Logo */}
                        <h1 onClick={() => navigate('/template18')} className="text-2xl lg:text-3xl font-light tracking-[0.25em] text-zinc-900 cursor-pointer">
                            AVANTE
                        </h1>

                        {/* Icons Right */}
                        <div className="flex items-center gap-5">
                            <Search className="w-5 h-5 text-zinc-700 cursor-pointer hover:text-zinc-900 transition-colors" />
                            <User className="w-5 h-5 text-zinc-700 cursor-pointer hover:text-zinc-900 transition-colors" onClick={() => navigate('/template18/profile')} />
                            <Heart className="w-5 h-5 text-zinc-700 cursor-pointer hover:text-zinc-900 transition-colors" />
                            <div className="relative cursor-pointer" onClick={() => navigate('/template18/cart')}>
                                <ShoppingBag className="w-5 h-5 text-zinc-700 hover:text-zinc-900 transition-colors" />
                                <span className="absolute -top-1 -right-1 w-4 h-4 bg-zinc-900 text-white text-[10px] rounded-full flex items-center justify-center">0</span>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            {/* ==================== HERO ==================== */}
            {/* Mobile Hero - current stacked design */}
            <section className="lg:hidden">
                <div className="relative aspect-[4/5]">
                    <img src="https://images.unsplash.com/photo-1496747611176-843222e1e57c?w=1920&q=80" alt="Vintage Collection" className="w-full h-full object-cover" />
                </div>
                <div className="bg-amber-50 px-5 py-8 text-center">
                    <p className="text-amber-600 text-[10px] tracking-[0.3em] mb-2" style={{ fontFamily: "'Georgia', serif" }}>THROWBACK COLLECTION '25</p>
                    <h2 className="text-2xl sm:text-3xl text-amber-950 mb-3 leading-tight" style={{ fontFamily: "'Georgia', serif" }}>Nostalgia Never<br />Looked So Good</h2>
                    <p className="text-[14px] text-amber-700 mb-6 max-w-sm mx-auto leading-relaxed">Vintage-inspired silhouettes reimagined with modern comfort. Every piece carries the charm of decades past.</p>
                    <button onClick={() => navigate('/template18/shop')} className="w-full py-4 bg-amber-900 text-amber-50 text-sm tracking-[0.2em] hover:bg-amber-800 transition-colors min-h-[48px] rounded-full">
                        EXPLORE THE COLLECTION
                    </button>
                </div>
            </section>

            {/* Desktop Hero - EXACT original slideshow */}
            <section className="hidden lg:block relative h-[95vh]">
                {heroSlides.map((slide, idx) => (
                    <div
                        key={idx}
                        className={`absolute inset-0 transition-opacity duration-1000 ${currentSlide === idx ? 'opacity-100' : 'opacity-0'}`}
                    >
                        <img src={slide.image} alt={slide.title} className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-r from-zinc-950/60 via-zinc-900/30 to-transparent"></div>
                    </div>
                ))}

                <div className="absolute inset-0 flex items-center">
                    <div className="max-w-screen-2xl mx-auto px-6 lg:px-12 w-full">
                        <div className="max-w-xl">
                            <p className="text-zinc-300 text-sm tracking-[0.4em] mb-4">{heroSlides[currentSlide].subtitle.toUpperCase()}</p>
                            <h2 className="text-5xl lg:text-7xl font-extralight text-white mb-8 leading-[1.1] tracking-wide">
                                {heroSlides[currentSlide].title}
                            </h2>
                            <button
                                onClick={() => navigate('/template18/shop')}
                                className="group px-10 py-4 bg-white text-zinc-900 text-sm tracking-[0.15em] hover:bg-zinc-100 transition-colors inline-flex items-center gap-4"
                            >
                                {heroSlides[currentSlide].cta.toUpperCase()}
                                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Slide Indicators */}
                <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex gap-3">
                    {heroSlides.map((_, idx) => (
                        <button
                            key={idx}
                            onClick={() => setCurrentSlide(idx)}
                            className={`h-0.5 transition-all duration-500 ${currentSlide === idx ? 'w-12 bg-white' : 'w-6 bg-white/40'}`}
                        />
                    ))}
                </div>
            </section>

            {/* ==================== CATEGORIES ==================== */}
            {/* Mobile Categories - horizontal scroll */}
            <section className="lg:hidden py-10 sm:py-16">
                <div className="max-w-screen-2xl mx-auto">
                    <div className="px-5 sm:px-6 mb-6 text-center">
                        <p className="text-amber-600 text-[10px] sm:text-xs tracking-[0.3em] mb-1">SHOP BY ERA</p>
                        <h2 className="text-xl sm:text-2xl text-amber-950" style={{ fontFamily: "'Georgia', serif" }}>Curated Collections</h2>
                    </div>
                    <div className="flex overflow-x-auto gap-3 px-5 snap-x snap-mandatory scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
                        {mobileCategories.map((cat, idx) => (
                            <div key={idx} className="w-[68vw] flex-shrink-0 snap-center group cursor-pointer">
                                <div className="relative aspect-[3/4] overflow-hidden rounded-2xl">
                                    <img src={cat.image} alt={cat.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-amber-950/70 via-transparent to-transparent" />
                                    <div className="absolute bottom-0 left-0 right-0 p-5">
                                        <h3 className="text-lg font-medium text-white" style={{ fontFamily: "'Georgia', serif" }}>{cat.name}</h3>
                                        <p className="text-amber-200/80 text-xs mt-1">{cat.tagline}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="px-5 mt-2">
                        <div className="w-16 h-0.5 bg-amber-200 rounded-full mx-auto"><div className="w-6 h-full bg-amber-600 rounded-full" /></div>
                    </div>
                </div>
            </section>

            {/* Desktop Category Grid - EXACT original */}
            <section className="hidden lg:block py-28">
                <div className="max-w-screen-2xl mx-auto px-12">
                    <div className="text-center mb-14">
                        <p className="text-zinc-500 text-xs tracking-[0.3em] mb-3">DISCOVER</p>
                        <h2 className="text-5xl font-extralight text-zinc-900 tracking-wide">Shop by Category</h2>
                    </div>

                    <div className="grid grid-cols-4 gap-6">
                        {desktopCategories.map((cat, idx) => (
                            <div key={idx} className="group cursor-pointer">
                                <div className="relative aspect-[3/4] overflow-hidden mb-4">
                                    <img src={cat.image} alt={cat.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                                    <div className="absolute inset-0 bg-zinc-900/0 group-hover:bg-zinc-900/20 transition-colors duration-500"></div>
                                </div>
                                <div className="text-center">
                                    <h3 className="text-lg font-light text-zinc-900 tracking-wide">{cat.name}</h3>
                                    <p className="text-xs text-zinc-500 mt-1">{cat.count} items</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ==================== VIDEO (desktop only) ==================== */}
            <section className="hidden lg:block relative h-[80vh] bg-zinc-900">
                <img
                    src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1600&h=900&fit=crop"
                    alt="Campaign Video"
                    className="w-full h-full object-cover opacity-70"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                        <button className="w-24 h-24 bg-white rounded-full flex items-center justify-center mb-8 mx-auto hover:scale-110 transition-transform shadow-2xl">
                            <Play className="w-10 h-10 text-zinc-800 ml-1" />
                        </button>
                        <p className="text-white/70 text-xs tracking-[0.4em] mb-3">CAMPAIGN VIDEO</p>
                        <h2 className="text-6xl font-extralight text-white tracking-wide">The New Chapter</h2>
                    </div>
                </div>
            </section>

            {/* ==================== PRODUCTS ==================== */}
            {/* Mobile Products - horizontal scroll with wishlist/quick-add */}
            <section className="lg:hidden py-10 sm:py-16 bg-amber-100/30">
                <div className="max-w-screen-2xl mx-auto">
                    <div className="flex items-end justify-between px-5 sm:px-6 mb-6 sm:mb-10">
                        <div>
                            <p className="text-amber-600 text-[10px] sm:text-xs tracking-[0.3em] mb-1">JUST ARRIVED</p>
                            <h2 className="text-xl sm:text-2xl text-amber-950" style={{ fontFamily: "'Georgia', serif" }}>New Arrivals</h2>
                        </div>
                    </div>
                    <div className="flex overflow-x-auto gap-3 px-5 snap-x snap-mandatory scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
                        {mobileProducts.map((product, idx) => (
                            <div key={idx} className="w-[44vw] flex-shrink-0 snap-start group">
                                <div className="relative aspect-[3/4] overflow-hidden rounded-xl mb-2 sm:mb-3 bg-amber-100/50">
                                    <img src={product.image} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                    {product.tag && <span className="absolute top-2 left-2 bg-amber-900 text-amber-50 text-[9px] sm:text-[10px] tracking-wider px-2 py-1 rounded-full">{product.tag}</span>}
                                    <button onClick={() => setWishlist(prev => ({ ...prev, [idx]: !prev[idx] }))} className="absolute top-2 right-2 w-8 h-8 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-sm">
                                        <Heart className={`w-3.5 h-3.5 ${wishlist[idx] ? 'fill-red-500 text-red-500' : 'text-amber-700'}`} />
                                    </button>
                                    <button className="absolute bottom-2 right-2 w-8 h-8 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-md sm:hidden">
                                        <Plus className="w-4 h-4 text-amber-900" />
                                    </button>
                                </div>
                                <p className="text-[9px] sm:text-[10px] text-amber-500 tracking-wider mb-0.5">{product.color.toUpperCase()}</p>
                                <h3 className="text-xs sm:text-sm text-amber-950 mb-0.5 leading-tight">{product.name}</h3>
                                <p className="text-xs sm:text-sm text-amber-700">{'\u20B9'}{product.price.toLocaleString()}</p>
                            </div>
                        ))}
                    </div>
                    <div className="text-center mt-6 px-5">
                        <button onClick={() => navigate('/template18/shop')} className="text-sm text-amber-900 tracking-wider border-b border-amber-400 pb-1 min-h-[44px] inline-flex items-center">View All Products</button>
                    </div>
                </div>
            </section>

            {/* Desktop Products - EXACT original grid */}
            <section className="hidden lg:block py-28 bg-zinc-50">
                <div className="max-w-screen-2xl mx-auto px-12">
                    <div className="flex items-end justify-between mb-14">
                        <div>
                            <p className="text-zinc-500 text-xs tracking-[0.3em] mb-3">FEATURED</p>
                            <h2 className="text-5xl font-extralight text-zinc-900 tracking-wide">New Arrivals</h2>
                        </div>
                        <button onClick={() => navigate('/template18/shop')} className="flex items-center gap-2 text-sm text-zinc-700 hover:text-zinc-900 transition-colors tracking-wide">
                            View All <ChevronRight className="w-4 h-4" />
                        </button>
                    </div>

                    <div className="grid grid-cols-4 gap-6">
                        {desktopProducts.map((product, idx) => (
                            <div
                                key={idx}
                                className="group"
                                onMouseEnter={() => setHoveredProduct(idx)}
                                onMouseLeave={() => setHoveredProduct(null)}
                            >
                                <div className="relative aspect-[3/4] overflow-hidden mb-4 bg-zinc-100">
                                    <img
                                        src={hoveredProduct === idx ? product.hoverImage : product.image}
                                        alt={product.name}
                                        className="w-full h-full object-cover transition-all duration-700"
                                    />
                                    {product.isNew && (
                                        <span className="absolute top-4 left-4 bg-zinc-900 text-white text-[10px] tracking-widest px-3 py-1.5">NEW</span>
                                    )}
                                    <button className="absolute top-4 right-4 w-9 h-9 bg-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 shadow-sm">
                                        <Heart className="w-4 h-4 text-zinc-700" />
                                    </button>
                                    <button className="absolute bottom-4 left-4 right-4 py-3.5 bg-white text-zinc-900 text-xs tracking-[0.15em] opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300 flex items-center justify-center gap-2">
                                        <Plus className="w-3 h-3" /> ADD TO BAG
                                    </button>
                                </div>
                                <div className="text-center">
                                    <p className="text-[11px] text-zinc-500 tracking-wider mb-1">{product.color.toUpperCase()}</p>
                                    <h3 className="text-sm text-zinc-900 mb-1">{product.name}</h3>
                                    <p className="text-sm text-zinc-700">₹{product.price.toLocaleString()}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ==================== BRAND STORY ==================== */}
            {/* Mobile Brand Story - full-bleed stacked */}
            <section className="lg:hidden py-10 sm:py-16">
                <div>
                    <img src="https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?w=1920&q=80" alt="Our Story" className="w-full aspect-[4/5] object-cover" />
                    <div className="px-5 py-8 bg-amber-50">
                        <p className="text-amber-600 text-[10px] tracking-[0.3em] mb-2">OUR STORY</p>
                        <h2 className="text-xl text-amber-950 mb-4 leading-relaxed" style={{ fontFamily: "'Georgia', serif" }}>Where Yesterday Meets Tomorrow</h2>
                        <p className="text-[14px] text-amber-700 leading-relaxed mb-4">Reverie was born from a love letter to the past. We scour vintage markets, antique stores, and family heirlooms to find patterns and silhouettes that deserve to be reimagined.</p>
                        <p className="text-[14px] text-amber-700 leading-relaxed mb-6">Each collection is a journey through decades of style, reinterpreted with sustainable fabrics and modern construction techniques.</p>
                        <button className="text-sm text-amber-900 tracking-[0.15em] border-b border-amber-900 pb-1 min-h-[44px] inline-flex items-center">READ OUR STORY</button>
                    </div>
                </div>
            </section>

            {/* Desktop Split Feature - EXACT original */}
            <section className="hidden lg:grid grid-cols-2">
                <div className="aspect-square lg:aspect-auto">
                    <img
                        src="https://images.unsplash.com/photo-1485968579580-b6d095142e6e?w=900&h=900&fit=crop"
                        alt="The Edit"
                        className="w-full h-full object-cover"
                    />
                </div>
                <div className="flex items-center justify-center py-0 px-20 bg-zinc-100">
                    <div className="max-w-md">
                        <p className="text-zinc-500 text-xs tracking-[0.3em] mb-4">THE AVANTE EDIT</p>
                        <h2 className="text-4xl font-extralight text-zinc-900 mb-6 leading-relaxed tracking-wide">
                            Curated for the Modern Woman
                        </h2>
                        <p className="text-zinc-600 leading-relaxed mb-8">
                            Discover our carefully curated selection of pieces designed for versatility and elegance.
                            Each item is chosen for its impeccable quality and timeless appeal.
                        </p>
                        <button className="text-sm text-zinc-900 tracking-[0.15em] border-b border-zinc-900 pb-1 hover:text-zinc-600 hover:border-zinc-600 transition-colors">
                            EXPLORE THE EDIT
                        </button>
                    </div>
                </div>
            </section>

            {/* ==================== TESTIMONIALS ==================== */}
            {/* Mobile Testimonials - swipeable carousel */}
            <section className="lg:hidden py-10 sm:py-16 bg-amber-900">
                <div className="max-w-screen-xl mx-auto px-5 sm:px-6">
                    <div className="text-center mb-8">
                        <p className="text-amber-400 text-[10px] sm:text-xs tracking-[0.3em] mb-2">LOVE LETTERS</p>
                        <h2 className="text-xl sm:text-2xl text-amber-50" style={{ fontFamily: "'Georgia', serif" }}>What They Say</h2>
                    </div>
                    <div className="overflow-hidden" onTouchStart={e => handleTestimonialSwipe(e, 'start')} onTouchEnd={e => handleTestimonialSwipe(e, 'end')}>
                        <div className="flex transition-transform duration-500 ease-out" style={{ transform: `translateX(-${activeTestimonial * 100}%)` }}>
                            {mobileTestimonials.map((t, idx) => (
                                <div key={idx} className="w-full flex-shrink-0 px-2">
                                    <div className="text-center">
                                        <div className="flex justify-center gap-1 mb-4">{[...Array(t.rating)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />)}</div>
                                        <p className="text-[14px] text-amber-100 leading-relaxed mb-5 italic">"{t.text}"</p>
                                        <p className="text-amber-50 text-sm">{t.name}</p>
                                        <p className="text-amber-400 text-xs mt-1">{t.city}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="flex justify-center gap-2 mt-6">
                            {mobileTestimonials.map((_, idx) => (
                                <button key={idx} onClick={() => setActiveTestimonial(idx)} className={`w-2 h-2 rounded-full transition-all ${activeTestimonial === idx ? 'bg-amber-400 w-6' : 'bg-amber-700'}`} />
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Desktop Testimonials - EXACT original grid */}
            <section className="hidden lg:block py-28">
                <div className="max-w-screen-xl mx-auto px-12">
                    <div className="text-center mb-14">
                        <p className="text-zinc-500 text-xs tracking-[0.3em] mb-3">TESTIMONIALS</p>
                        <h2 className="text-5xl font-extralight text-zinc-900 tracking-wide">What They Say</h2>
                    </div>

                    <div className="grid grid-cols-3 gap-12">
                        {desktopTestimonials.map((item, idx) => (
                            <div key={idx} className="text-center">
                                <div className="flex justify-center gap-1 mb-6">
                                    {[...Array(item.rating)].map((_, i) => (
                                        <Star key={i} className="w-4 h-4 fill-zinc-800 text-zinc-800" />
                                    ))}
                                </div>
                                <p className="text-zinc-700 leading-relaxed mb-6 italic text-lg font-light">"{item.text}"</p>
                                <p className="text-zinc-900 tracking-wide">{item.name}</p>
                                <p className="text-zinc-500 text-sm">{item.city}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ==================== FEATURES (mobile only) ==================== */}
            <section className="lg:hidden py-10 sm:py-16 bg-amber-50">
                <div className="max-w-screen-2xl mx-auto">
                    <div className="px-5 sm:px-6 mb-6 text-center">
                        <h2 className="text-xl sm:text-2xl text-amber-950" style={{ fontFamily: "'Georgia', serif" }}>The Reverie Promise</h2>
                    </div>
                    <div className="flex overflow-x-auto gap-4 px-5 snap-x snap-mandatory scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
                        {features.map((f, idx) => (
                            <div key={idx} className="w-[60vw] flex-shrink-0 snap-center text-center p-6 bg-white rounded-xl border border-amber-200/50">
                                <f.icon className="w-6 h-6 text-amber-700 mx-auto mb-3" strokeWidth={1.5} />
                                <h3 className="text-sm sm:text-base font-medium text-amber-950 mb-1">{f.title}</h3>
                                <p className="text-[14px] text-amber-600">{f.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ==================== INSTAGRAM (mobile only) ==================== */}
            <section className="lg:hidden py-10 sm:py-16">
                <div className="max-w-screen-2xl mx-auto px-5 sm:px-6">
                    <div className="text-center mb-6">
                        <div className="flex items-center justify-center gap-2 mb-2">
                            <Instagram className="w-4 h-4 text-amber-700" />
                            <span className="text-xs text-amber-600 tracking-[0.2em]">@reverie.vintage</span>
                        </div>
                        <h2 className="text-xl sm:text-2xl text-amber-950" style={{ fontFamily: "'Georgia', serif" }}>Throwback Moments</h2>
                    </div>
                    <div className="grid grid-cols-3 gap-1 sm:gap-1.5">
                        {instagramPosts.map((post, idx) => (
                            <div key={idx} className="aspect-square group cursor-pointer overflow-hidden rounded-lg">
                                <img src={post} alt={`Vintage ${idx + 1}`} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ==================== NEWSLETTER ==================== */}
            {/* Mobile Newsletter */}
            <section className="lg:hidden py-10 sm:py-16 bg-amber-900 text-amber-50">
                <div className="max-w-xl mx-auto px-5 sm:px-6 text-center">
                    <h2 className="text-xl sm:text-2xl mb-3" style={{ fontFamily: "'Georgia', serif" }}>Join the Nostalgia Club</h2>
                    <p className="text-amber-300 text-[14px] sm:text-sm mb-6 sm:mb-8">Get early access to new drops, vintage finds, and a 10% welcome discount on your first order.</p>
                    <div className="flex flex-col sm:flex-row gap-3">
                        <input type="email" placeholder="Your email address" className="flex-1 px-5 py-4 bg-amber-800 border border-amber-700 text-amber-50 text-sm placeholder-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500 rounded-full sm:rounded-lg min-h-[48px]" />
                        <button className="px-8 py-4 bg-amber-50 text-amber-900 text-xs tracking-[0.15em] font-medium hover:bg-white transition-colors w-full sm:w-auto rounded-full sm:rounded-lg min-h-[48px]">SUBSCRIBE</button>
                    </div>
                    <p className="text-amber-600 text-xs mt-4">No spam. Unsubscribe anytime.</p>
                </div>
            </section>

            {/* Desktop Newsletter - EXACT original */}
            <section className="hidden lg:block py-24 bg-zinc-900 text-white">
                <div className="max-w-xl mx-auto px-6 text-center">
                    <p className="text-zinc-400 text-xs tracking-[0.3em] mb-3">NEWSLETTER</p>
                    <h2 className="text-4xl font-extralight mb-4 tracking-wide">Stay Connected</h2>
                    <p className="text-zinc-400 text-sm mb-8">
                        Subscribe for exclusive access to new collections, private sales, and style updates.
                    </p>
                    <div className="flex gap-3">
                        <input
                            type="email"
                            placeholder="Enter your email"
                            className="flex-1 px-5 py-4 bg-zinc-800 border-0 text-white text-sm placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-zinc-600"
                        />
                        <button className="px-8 py-4 bg-white text-zinc-900 text-xs tracking-[0.15em] hover:bg-zinc-200 transition-colors">
                            SUBSCRIBE
                        </button>
                    </div>
                </div>
            </section>

            {/* ==================== FOOTER ==================== */}
            {/* Mobile Footer */}
            <footer className="lg:hidden bg-amber-50 border-t border-amber-200">
                <div className="max-w-screen-2xl mx-auto px-5 sm:px-6 py-10 sm:py-16">
                    <div className="grid grid-cols-2 gap-8">
                        <div className="col-span-2">
                            <h3 className="text-xl tracking-[0.15em] mb-4 text-amber-900" style={{ fontFamily: "'Georgia', serif" }}>REVERIE</h3>
                            <p className="text-amber-700 text-sm leading-relaxed">Vintage-inspired fashion for the modern nostalgist. Crafted with love in India.</p>
                        </div>
                        <div>
                            <h4 className="text-[10px] tracking-[0.2em] mb-4 text-amber-500">SHOP</h4>
                            <ul className="space-y-2 text-sm">
                                <li><span onClick={() => navigate('/template18/shop')} className="text-amber-700 hover:text-amber-950 cursor-pointer transition-colors min-h-[44px] inline-flex items-center">All Products</span></li>
                                <li><span className="text-amber-700 hover:text-amber-950 cursor-pointer transition-colors min-h-[44px] inline-flex items-center">New Arrivals</span></li>
                                <li><span className="text-amber-700 hover:text-amber-950 cursor-pointer transition-colors min-h-[44px] inline-flex items-center">Bestsellers</span></li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="text-[10px] tracking-[0.2em] mb-4 text-amber-500">HELP</h4>
                            <ul className="space-y-2 text-sm">
                                <li><span onClick={() => navigate('/template18/contact')} className="text-amber-700 hover:text-amber-950 cursor-pointer transition-colors min-h-[44px] inline-flex items-center">Contact</span></li>
                                <li><span onClick={() => navigate('/template18/orders')} className="text-amber-700 hover:text-amber-950 cursor-pointer transition-colors min-h-[44px] inline-flex items-center">Track Order</span></li>
                                <li><span className="text-amber-700 hover:text-amber-950 cursor-pointer transition-colors min-h-[44px] inline-flex items-center">Returns</span></li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="text-[10px] tracking-[0.2em] mb-4 text-amber-500">CONNECT</h4>
                            <ul className="space-y-2 text-sm">
                                <li className="text-amber-700">hello@reverie.in</li>
                                <li className="text-amber-700">+91 98765 43210</li>
                                <li className="text-amber-700 pt-1">Mon - Sat: 10am - 7pm</li>
                            </ul>
                        </div>
                    </div>
                    <div className="border-t border-amber-200 mt-8 pt-6 flex flex-col items-center gap-4">
                        <p className="text-amber-500 text-xs">&copy; 2025 Reverie. All rights reserved.</p>
                        <div className="flex gap-6 text-xs text-amber-500">
                            <span className="hover:text-amber-900 cursor-pointer transition-colors min-h-[44px] inline-flex items-center">Privacy</span>
                            <span className="hover:text-amber-900 cursor-pointer transition-colors min-h-[44px] inline-flex items-center">Terms</span>
                        </div>
                    </div>
                </div>
            </footer>

            {/* Desktop Footer - EXACT original */}
            <footer className="hidden lg:block bg-white border-t border-zinc-200">
                <div className="max-w-screen-2xl mx-auto px-12 py-20">
                    <div className="grid grid-cols-4 gap-16">
                        <div>
                            <h3 className="text-xl font-light tracking-[0.2em] mb-6">AVANTE</h3>
                            <p className="text-zinc-600 text-sm leading-relaxed">
                                Refined elegance for the modern woman. Premium fashion crafted with intention.
                            </p>
                        </div>

                        <div>
                            <h4 className="text-xs tracking-[0.15em] mb-5 text-zinc-500">SHOP</h4>
                            <ul className="space-y-3 text-sm">
                                <li><span onClick={() => navigate('/template18/shop')} className="text-zinc-700 hover:text-zinc-900 cursor-pointer transition-colors">All Products</span></li>
                                <li><span className="text-zinc-700 hover:text-zinc-900 cursor-pointer transition-colors">New Arrivals</span></li>
                                <li><span className="text-zinc-700 hover:text-zinc-900 cursor-pointer transition-colors">Collections</span></li>
                                <li><span className="text-zinc-700 hover:text-zinc-900 cursor-pointer transition-colors">Sale</span></li>
                            </ul>
                        </div>

                        <div>
                            <h4 className="text-xs tracking-[0.15em] mb-5 text-zinc-500">HELP</h4>
                            <ul className="space-y-3 text-sm">
                                <li><span onClick={() => navigate('/template18/contact')} className="text-zinc-700 hover:text-zinc-900 cursor-pointer transition-colors">Contact Us</span></li>
                                <li><span onClick={() => navigate('/template18/orders')} className="text-zinc-700 hover:text-zinc-900 cursor-pointer transition-colors">Track Order</span></li>
                                <li><span className="text-zinc-700 hover:text-zinc-900 cursor-pointer transition-colors">Shipping</span></li>
                                <li><span className="text-zinc-700 hover:text-zinc-900 cursor-pointer transition-colors">Returns</span></li>
                            </ul>
                        </div>

                        <div>
                            <h4 className="text-xs tracking-[0.15em] mb-5 text-zinc-500">CONNECT</h4>
                            <ul className="space-y-3 text-sm">
                                <li className="text-zinc-700">hello@avante.in</li>
                                <li className="text-zinc-700">+91 98765 43210</li>
                                <li className="text-zinc-700 pt-2">Mon - Sat: 10am - 7pm</li>
                            </ul>
                        </div>
                    </div>

                    <div className="border-t border-zinc-200 mt-12 pt-8 flex justify-between items-center gap-4">
                        <p className="text-zinc-500 text-xs">© 2025 Avante. All rights reserved.</p>
                        <div className="flex gap-6 text-xs text-zinc-500">
                            <span className="hover:text-zinc-900 cursor-pointer transition-colors">Privacy Policy</span>
                            <span className="hover:text-zinc-900 cursor-pointer transition-colors">Terms of Service</span>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Template18;